import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Power, Shield, Clock, Mail, AlertTriangle, 
  CheckCircle, Activity, TrendingUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EscalationSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [activeEscalations, setActiveEscalations] = useState([]);
  const [history, setHistory] = useState([]);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchActiveEscalations();
    fetchHistory();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/escalation/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      } else {
        toast.error('Failed to load escalation settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveEscalations = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/escalation/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setActiveEscalations(data.escalations || []);
      }
    } catch (error) {
      console.error('Error fetching active escalations:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/escalation/history?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const toggleEscalation = async () => {
    try {
      setToggling(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const newState = !settings.enabled;

      const response = await fetch(`${API_URL}/api/monitoring/escalation/toggle?enabled=${newState}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        toast.success(data.message);
        
        // Refresh active escalations
        fetchActiveEscalations();
      } else {
        toast.error('Failed to toggle escalation workflow');
      }
    } catch (error) {
      console.error('Error toggling escalation:', error);
      toast.error('Error toggling escalation workflow');
    } finally {
      setToggling(false);
    }
  };

  const getEscalationLevelBadge = (level) => {
    const levels = {
      1: { text: 'L1: Ticket Created', color: 'bg-blue-100 text-blue-800' },
      2: { text: 'L2: Support Escalated', color: 'bg-orange-100 text-orange-800' },
      3: { text: 'L3: Manager Notified', color: 'bg-red-100 text-red-800' }
    };
    return levels[level] || levels[1];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading escalation settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      {/* Header */}
      <div className="mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Shield className="h-6 w-6" />
                Automated Escalation Workflow
              </h1>
              <p className="text-purple-100 text-sm mt-1">
                Automatic ticket creation and escalation based on downtime
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Toggle Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <Card className={`border-2 ${settings?.enabled ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Power className={`h-6 w-6 ${settings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                  <span>Escalation Workflow Status</span>
                </div>
                <Button
                  onClick={toggleEscalation}
                  disabled={toggling}
                  className={`${
                    settings?.enabled 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-gray-400 hover:bg-gray-500'
                  } text-white`}
                >
                  {toggling ? 'Updating...' : settings?.enabled ? 'ON' : 'OFF'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${settings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <p className="font-medium text-sm mb-2">
                    {settings?.enabled ? '✅ Escalation workflow is ACTIVE' : '⚠️ Escalation workflow is DISABLED'}
                  </p>
                  <p className="text-xs text-gray-700">
                    {settings?.enabled 
                      ? 'System is automatically monitoring customer downtime and creating tickets with escalations.' 
                      : 'Turn ON to enable automatic ticket creation and escalation for inactive customers.'}
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Escalation Timeline
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</div>
                      <div>
                        <p className="text-sm font-medium">30 minutes downtime</p>
                        <p className="text-xs text-gray-600">→ Automatic ticket creation</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</div>
                      <div>
                        <p className="text-sm font-medium">1 hour downtime</p>
                        <p className="text-xs text-gray-600">→ Escalate to L2 Support ({settings?.l2_email})</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</div>
                      <div>
                        <p className="text-sm font-medium">4 hours downtime</p>
                        <p className="text-xs text-gray-600">→ Notify Manager ({settings?.manager_email})</p>
                        <p className="text-xs text-gray-500">CC: {settings?.cc_emails?.join(', ')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Summary */}
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Active Escalations</div>
                  <div className="text-3xl font-bold">{activeEscalations.length}</div>
                </div>
                <AlertTriangle className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Tickets (7 days)</div>
                  <div className="text-3xl font-bold">{history.length}</div>
                </div>
                <TrendingUp className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium">Email Recipients</span>
              </div>
              <div className="text-xs space-y-1 text-gray-600">
                <div>L2: {settings?.l2_email}</div>
                <div>Manager: {settings?.manager_email}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Active Escalations */}
      {activeEscalations.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Currently Active Escalations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeEscalations.map((escalation, idx) => {
                const hours = escalation.downtime_hours;
                let level = 1;
                if (escalation.escalated_manager) level = 3;
                else if (escalation.escalated_l2) level = 2;
                
                const levelInfo = getEscalationLevelBadge(level);

                return (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{escalation.customer_name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${levelInfo.color}`}>
                        {levelInfo.text}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>IP: {escalation.static_ip}</div>
                      <div className="font-medium text-red-600">
                        Down for: {hours.toFixed(1)} hours ({escalation.downtime_minutes.toFixed(0)} minutes)
                      </div>
                      {escalation.ticket_id && (
                        <div className="text-xs text-blue-600">Ticket: {escalation.ticket_id}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-indigo-600" />
            Recent Escalation History (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No escalation tickets in the past 7 days</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.slice(0, 10).map((ticket, idx) => {
                const levelInfo = getEscalationLevelBadge(ticket.escalation_level || 1);
                return (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-sm">{ticket.customer_name}</h4>
                        <p className="text-xs text-gray-500">{ticket.ticket_id}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${levelInfo.color}`}>
                        {levelInfo.text}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      <div>IP: {ticket.static_ip}</div>
                      <div>Created: {new Date(ticket.created_at).toLocaleString()}</div>
                      <div className="capitalize">Status: {ticket.status}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EscalationSettings;
